import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import axios from 'axios';

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    fetchNews();
    getUserRole();
  }, []);

  const getUserRole = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setUserRole('guest');
        return;
      }

      const response = await axios.get(`${API_URL}/api/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setUserRole(response.data.data.role);
      } else {
        setUserRole('guest');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole('guest');
    }
  };

  const fetchNews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Получаем все новости
      const response = await axios.get(`${API_URL}/api/news/public`, {
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });

      if (response.data.success) {
        // Фильтруем только новости с contentType === 'news'
        const newsOnly = response.data.data.filter(item =>
          !item.settings?.contentType || item.settings.contentType === 'news'
        );
        setNews(newsOnly);
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Ошибка загрузки новостей');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-xl text-gray-600">Загрузка новостей...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            📰 Новости компании
          </h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-center">{error}</p>
            </div>
          )}

          {news.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📭</div>
              <p className="text-xl text-gray-600">Пока нет новостей</p>
              <p className="text-gray-500 mt-2">Новости появятся здесь, как только администратор их опубликует</p>
            </div>
          ) : (
            <div className="space-y-6">
              {news.map((newsItem) => (
                <div
                  key={newsItem.id}
                  className="bg-gradient-to-r from-blue-50 to-white border border-blue-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        {newsItem.type === 'text' ? (
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-semibold">📝</span>
                          </div>
                        ) : (
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <span className="text-green-600 font-semibold">📊</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          {newsItem.type === 'text' ? 'Текстовая новость' : 'Новость по отчету'}
                        </p>
                        <p className="text-sm font-medium text-gray-700">
                          {formatDate(newsItem.createdAt)}
                        </p>
                      </div>
                    </div>

                    {newsItem.settings?.priority && newsItem.settings.priority > 7 && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        🔥 Важно
                      </span>
                    )}
                  </div>

                  <div className="prose prose-sm max-w-none">
                    <div className="text-gray-800 leading-relaxed whitespace-pre-line">
                      {newsItem.title && (
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {newsItem.title}
                        </h3>
                      )}
                      {newsItem.text}
                    </div>
                  </div>

                  {/* Отображение медиафайла */}
                  {newsItem.mediaFile && newsItem.mediaFile.url && (
                    <div className="mt-4">
                      {newsItem.mediaFile.type && newsItem.mediaFile.type.startsWith('image/') ? (
                        <img
                          src={newsItem.mediaFile.url}
                          alt={newsItem.mediaFile.originalName}
                          className="w-full max-w-md rounded-lg shadow-sm"
                        />
                      ) : newsItem.mediaFile.type && newsItem.mediaFile.type.startsWith('video/') ? (
                        <video
                          src={newsItem.mediaFile.url}
                          controls
                          className="w-full max-w-md rounded-lg shadow-sm"
                        >
                          Ваш браузер не поддерживает воспроизведение видео.
                        </video>
                      ) : null}
                      {newsItem.mediaFile.originalName && (
                        <p className="text-xs text-gray-500 mt-2">
                          {newsItem.mediaFile.originalName}
                        </p>
                      )}
                    </div>
                  )}

                  {newsItem.attachments && newsItem.attachments.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600 mb-2">Вложения:</p>
                      <div className="flex flex-wrap gap-2">
                        {newsItem.attachments.map((attachment, index) => (
                          <a
                            key={index}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm hover:bg-blue-200 transition-colors"
                          >
                            📎 {attachment.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="text-center text-sm text-gray-500 mt-6">
          <p>Обновляется автоматически</p>
          <p>Роль: <span className="font-medium text-gray-700">{userRole}</span></p>
        </div>
      </div>
    </div>
  );
};

export default NewsPage;